import { useState, useRef, useEffect } from 'react'
import { cn } from '@/lib/utils'

type ToggleContainerProps = {
  children: React.ReactNode
  center?: boolean
  className?: string
}

export default function Toggle({ children, center, className }: ToggleContainerProps) {
  const [isSticky, setIsSticky] = useState(false)
  const observerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!observerRef.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting)
      },
      {
        threshold: [1],
        rootMargin: '-10px 0px 0px 0px',
      },
    )

    observer.observe(observerRef.current)

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <>
      <div ref={observerRef} className="absolute h-px w-px"></div>
      <div
        className={cn(
          'hide-scrollbar sticky top-[135px] z-40 w-full bg-white/95 backdrop-blur-md dark:bg-black/95 md:top-20',
          'before:transition-width before:absolute before:inset-x-0 before:bottom-0 before:border-b-2 before:border-zinc-100 before:duration-300 dark:before:border-white/20',
          'after:pointer-events-none after:absolute after:-bottom-10 after:left-0 after:z-40 after:h-10 after:w-full after:bg-gradient-to-b after:from-white after:from-25% after:to-transparent dark:after:from-black',
          'after:transition-opacity after:duration-300',
          isSticky
            ? 'before:left-0 before:w-full after:opacity-100'
            : 'before:left-2 before:w-[calc(100%-16px)] after:opacity-0',
          center && `mx-auto sm:w-fit md:w-full ${!isSticky && 'max-w-content'}`,
          className,
        )}
      >
        {children}
      </div>
    </>
  )
}
